import { WeatherServiceParams } from '../types/weather';
import { validateWeatherParams } from '../utils/validation';
import { processPolygonData } from '../utils/polygonProcessor';
import { handleWeatherError } from '../utils/errorHandler';
import { fetchXWeatherData } from './providers/xWeatherService';
import { fetchOpenMeteoData } from './providers/openMeteoService';
import { fetchDroughtData } from './providers/droughtService';
import { supabase } from '../lib/supabase';

export async function fetchWeatherData(params: WeatherServiceParams) {
  try {
    // Check credits first
    const { data: credits } = await supabase
      .from('user_credits')
      .select('credits')
      .single();

    if (!credits || credits.credits <= 0) {
      throw new Error('Insufficient credits to perform search');
    }

    // Skip validation for shipping model
    if (params.model !== 'shipping') {
      validateWeatherParams(params);
    }

    // Process polygon data for non-drought and non-shipping models
    let polygons = [];
    if (params.model !== 'drought' && params.model !== 'shipping') {
      const data = params.drawInstance!.getAll();
      polygons = processPolygonData(data);
      
      if (polygons.length === 0) {
        throw new Error('Please draw at least one polygon on the map');
      }
    }

    // Deduct credits before fetching data
    const { error: deductError } = await supabase.rpc('deduct_credits', {
      user_id: (await supabase.auth.getUser()).data.user?.id,
      amount_to_deduct: 1
    });

    if (deductError) {
      throw new Error('Failed to deduct credits');
    }

    // Get updated credits after deduction
    if (params.onCreditUpdate) {
      await params.onCreditUpdate();
    }

    // Fetch data based on model
    let result;
    try {
      switch (params.model) {
        case 'shipping':
          // For shipping model, fetch tropical cyclones data for predefined area
          const shippingArea = {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [[
                [-98, 18], // Gulf of Mexico SW
                [-98, 45], // North America
                [-60, 45], // North Atlantic
                [-60, 18], // Caribbean
                [-98, 18]  // Back to start
              ]]
            }
          };
          result = await fetchXWeatherData([shippingArea], {
            ...params,
            parameterSelect: 'tropicalcyclones'
          });
          break;
        case 'drought':
          result = await fetchDroughtData(polygons, params);
          break;
        case 'xweather':
          result = await fetchXWeatherData(polygons, params);
          break;
        default:
          result = await fetchOpenMeteoData(polygons, {
            ...params,
            onProgress: (progress) => {
              if (params.setLoadingProgress) {
                params.setLoadingProgress(progress);
              }
            }
          });
      }
    } catch (error) {
      console.error('Data fetch error:', error);
      throw error;
    }

    if (!result || (Array.isArray(result) && result.length === 0)) {
      throw new Error('No data found for the selected area and time period');
    }

    return result;
  } catch (error) {
    console.error('Weather service error:', error);
    throw handleWeatherError(error);
  }
}